'use client';

/**
 * WebTopNavCard — proto-header for mode7 sub-page navigation.
 *
 * Seed of the panels-as-cards header. Today it's a sticky top strip
 * inside the canvas_2 scroll, with brand text + N selection-flipping
 * links + a primary CTA. When the source's full Header (Platform
 * dropdown, FAQ, Resources, theme toggle, Book Demo) lands, this
 * graduates to a content-driven card with a Pydantic schema and a BFF
 * strategy. For two links it'd be more boilerplate than substance —
 * hardcoded suffices.
 *
 * Architectural posture:
 *   - Mounted as a ContainerPlacement inside both web_landing_container
 *     and web_for_buyers_container — same nav across mode7 selections.
 *   - Reads/writes layoutStore.explorerSelection. Click switches
 *     selection; the AppShell re-resolves the matrix and the canvas
 *     remounts the new container card.
 *   - position: sticky inside the canvas_2 scroll viewport so it stays
 *     visible while the user scrolls long pages.
 *   - .web-surface palette boundary so it inherits the website's
 *     magenta primary, not the IPE workspace blue.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useEffect, useRef, useState } from 'react';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useDevUserStore } from '@/lib/stores/dev-user-store';
import { useCardTypography } from '@/lib/design/card-chrome';
import { MagicLinkForm } from '@/components/auth/magic-link-form';
import { cn } from '@/lib/utils/cn';


interface NavLink {
  label: string;
  /** explorerSelection value. null = the mode's default (web_landing). */
  selection: string | null;
}

const LINKS: NavLink[] = [
  { label: 'Home',             selection: null            },
  { label: 'For Buyers',       selection: 'for_buyers'    },
  { label: 'For Vendors',      selection: 'for_suppliers' },
  { label: 'Resources',        selection: 'resources'     },
  { label: 'Ask Klyde',        selection: 'reception'     },
];


export function WebTopNavCard() {
  const T = useCardTypography();
  const explorerSelection = useLayoutStore((s) => s.explorerSelection);
  const setExplorerSelection = useLayoutStore((s) => s.setExplorerSelection);

  // Sign-in affordance: visible only while the active session is the
  // anonymous SB_PUBLIC Actor (role=public). Authenticated users see no
  // Sign-in CTA here; their persona-switching lives in the IPE shell.
  const isPublic = useDevUserStore((s) => s.selectedUser?.role === 'public');
  const [signInOpen, setSignInOpen] = useState(false);
  const signInRef = useRef<HTMLDivElement>(null);

  // Outside-click dismiss for the Sign-in popover. The popover sits
  // inside `signInRef`; any mousedown outside collapses it. Keyed on
  // `signInOpen` so the listener only attaches while the popover is
  // open — keeps the no-popover path zero-cost.
  useEffect(() => {
    if (!signInOpen) return;
    const handler = (e: MouseEvent) => {
      if (signInRef.current && !signInRef.current.contains(e.target as Node)) {
        setSignInOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [signInOpen]);

  return (
    <div
      className={cn(
        'web-surface sticky top-0 z-30 w-full',
        'bg-background/90 backdrop-blur-xl border-b border-border/40',
      )}
    >
      <div className="container mx-auto flex items-center justify-between px-6 py-4">
        <button
          type="button"
          onClick={() => setExplorerSelection(null)}
          className="text-base font-semibold tracking-tight text-foreground hover:text-primary transition-colors"
        >
          Klyde
        </button>

        <nav className="flex items-center gap-6">
          {LINKS.map((link) => {
            const active = explorerSelection === link.selection;
            return (
              <button
                key={link.label}
                type="button"
                onClick={() => setExplorerSelection(link.selection)}
                className={cn(
                  'text-sm transition-colors',
                  active
                    ? 'text-foreground font-medium'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {link.label}
              </button>
            );
          })}
          {isPublic && (
            <div ref={signInRef} className="relative">
              <button
                type="button"
                onClick={() => setSignInOpen((o) => !o)}
                className={cn(
                  'text-sm transition-colors',
                  signInOpen
                    ? 'text-foreground font-medium'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                Sign in
              </button>
              {signInOpen && (
                <div className="absolute right-0 top-full mt-3 w-72 rounded-lg border border-border bg-card p-4 shadow-lg z-50">
                  <p className="text-xs text-muted-foreground mb-2">
                    We&apos;ll email you a one-click sign-in link.
                  </p>
                  <MagicLinkForm onClose={() => setSignInOpen(false)} />
                </div>
              )}
            </div>
          )}
          <a
            href="mailto:hello@klyde.com.au?subject=Join the Klyde Waitlist"
            className="inline-flex items-center rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/25 transition-all"
          >
            Join the Waitlist
          </a>
        </nav>
      </div>
    </div>
  );
}
